import { ARM_ROWS } from "./arms.js";
import { DEFAULT_RULE_LIMITS, PROTECT_HOLD_MINUTES } from "./limits.js";
export const CALM = { acute: false, acuteSource: null, windDown: false, source: null };
/** Consecutive days a metric has sat the same side of this member's own usual. */
export function runDays(body, kind, direction) {
    return body.runs?.find((r) => r.kind === kind && r.direction === direction)?.days ?? 0;
}
/** A day is heavy when it runs this much above the member's usual. */
export const HEAVY_DAY_RATIO = 1.25;
/** A calendar shape needs less sitting behind it than a quiet day does. */
const SEATED_SHARE = 0.6;
/** By early afternoon, this little of a usual day reads as a day not started. */
const MIDDAY_FLOOR_RATIO = 0.12;
/** Below this a step baseline is a partial sync or a member barely wearing the device, not a usual day. */
export const MIN_USUAL_STEPS = 2000;
/** Below this, a day is short whatever the member's usual looks like. */
const HEAVY_DAY_FLOOR_MINUTES = 120;
export function buildForecast(blocks, limits = DEFAULT_RULE_LIMITS) {
    const sorted = [...blocks].sort((a, b) => a.startMinutes - b.startMinutes);
    let meetingMinutes = 0;
    let coveredTo = Number.NEGATIVE_INFINITY;
    for (const b of sorted) {
        const from = Math.max(b.startMinutes, coveredTo);
        if (b.endMinutes > from) {
            meetingMinutes += b.endMinutes - from;
            coveredTo = b.endMinutes;
        }
    }
    const gaps = [];
    let longestRun = 0;
    let run = 0;
    let bookedTo = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < sorted.length; i++) {
        const block = sorted[i];
        const next = sorted[i + 1];
        if (!block)
            continue;
        bookedTo = Math.max(bookedTo, block.endMinutes);
        run += 1;
        if (!next) {
            longestRun = Math.max(longestRun, run);
            break;
        }
        const gap = next.startMinutes - bookedTo;
        if (gap <= limits.backToBackMinutes) {
            if (gap >= limits.usableGapMinutes) {
                gaps.push({
                    startMinutes: bookedTo,
                    endMinutes: next.startMinutes,
                    durationMinutes: gap,
                });
            }
            continue;
        }
        longestRun = Math.max(longestRun, run);
        run = 0;
        gaps.push({
            startMinutes: bookedTo,
            endMinutes: next.startMinutes,
            durationMinutes: gap,
        });
    }
    return {
        meetingMinutes,
        blockCount: sorted.length,
        longestRun,
        gaps,
        recoveryDebt: recoveryDebt(meetingMinutes, gaps),
    };
}
function recoveryDebt(meetingMinutes, gaps) {
    const given = gaps.reduce((sum, g) => sum + g.durationMinutes, 0);
    return Math.max(0, meetingMinutes - given * 2);
}
/** When the member's day stops being open, where they have not said. */
const OPEN_DAY_END_MINUTES = 21 * 60;
// The window in which a low step count is worth a walk: late morning to mid afternoon.
const MIDDAY_FROM_MINUTES = 11 * 60;
const MIDDAY_TO_MINUTES = 15 * 60;
// Sunset this close is the last of the light; further out, there is no reason to say so yet.
const DAYLIGHT_WINDOW_MINUTES = 60;
// Moving a meeting shorter than this gives back less than the ask costs.
const WORTH_MOVING_MINUTES = 25;
export function readDay(forecast, body, limits, 
/** Minutes since the member's local midnight. Gaps behind them are not offers. */
nowMinutes = Number.NEGATIVE_INFINITY, 
/** Today's blocks, so a run that already ended cannot unlock a hold. */
blocks, 
/** What a working day usually costs this member, in meeting minutes. */
typicalMeetingMinutes, 
/** Tomorrow's blocks, already parsed. Prediction is a lookup, not a model. */
tomorrow, outOfOffice = false, 
/** The member's own end of day, from their quiet hours. Null falls back to the fleet hour. */
dayEndMinutes) {
    const dayEnd = dayEndMinutes ?? OPEN_DAY_END_MINUTES;
    // The fixed limits are the fallback for a member with no step history yet.
    const usualSteps = body.usualSteps != null && body.usualSteps >= MIN_USUAL_STEPS ? body.usualSteps : null;
    const stepCeiling = usualSteps != null ? Math.round(usualSteps) : limits.stepCeiling;
    const middayFloor = usualSteps != null ? Math.round(usualSteps * MIDDAY_FLOOR_RATIO) : limits.middayStepFloor;
    // Their own sit, not a fixed bar: ten minutes is a long sit for one member
    // and nothing for another.
    const idleThreshold = body.typicalSitMinutes ?? limits.idleMinutes;
    const idle = (body.idleMinutes ?? 0) >= idleThreshold;
    const lowMidday = body.stepsToday != null &&
        body.stepsToday < middayFloor &&
        nowMinutes >= MIDDAY_FROM_MINUTES &&
        nowMinutes < MIDDAY_TO_MINUTES;
    const stress = body.stress ?? CALM;
    const stressed = stress.acute || stress.windDown;
    const weather = body.weather ?? null;
    const clearsAt = weather?.rainClearsAtMinutes ?? null;
    const clearing = clearsAt !== null &&
        weather?.outdoorOk !== false &&
        (nextUsableGap(forecast, clearsAt, limits, blocks) !== null ||
            dayOpenAt(blocks, clearsAt, dayEnd));
    const untilSunset = weather?.sunsetMinutes != null ? weather.sunsetMinutes - nowMinutes : null;
    const lastLight = untilSunset !== null &&
        untilSunset > 0 &&
        untilSunset <= DAYLIGHT_WINDOW_MINUTES &&
        weather?.outdoorOk !== false &&
        weather?.raining !== true &&
        body.walkedToday !== true;
    // A heavy day is its own reason to look, without a body reading behind it.
    const heavyThreshold = typicalMeetingMinutes != null && typicalMeetingMinutes > 0
        ? Math.min(limits.heavyDayMinutes, Math.max(HEAVY_DAY_FLOOR_MINUTES, Math.round(typicalMeetingMinutes * HEAVY_DAY_RATIO)))
        : limits.heavyDayMinutes;
    const heavyDay = forecast.meetingMinutes >= heavyThreshold;
    // Idle, a low step count and the stress reads each stand on their own
    // signal; only the load-shaped arms need a depletion score behind them.
    const eligible = !(body.depletion === null &&
        !idle &&
        !lowMidday &&
        !stressed &&
        !clearing &&
        !lastLight &&
        !heavyDay);
    // Not in a meeting and nothing booked ahead is an open day until the evening, whether the calendar is empty or its last meeting has finished.
    const restOfDayOpen = dayOpenAt(blocks, nowMinutes, dayEnd);
    const nextGap = restOfDayOpen ? null : nextUsableGap(forecast, nowMinutes, limits, blocks);
    const openNow = restOfDayOpen ||
        (nextGap !== null && (!Number.isFinite(nowMinutes) || nextGap.startMinutes <= nowMinutes));
    const run = blocks ? longestRunAhead(blocks, nowMinutes, limits) : null;
    const protectSlot = blocks && run
        ? freeWindowBefore(blocks, run.start.startMinutes, PROTECT_HOLD_MINUTES, nowMinutes)
        : null;
    const moveTarget = blocks ? leastImpactfulBlock(blocks, nowMinutes) : null;
    const enoughToday = body.stepsToday != null && body.stepsToday >= stepCeiling;
    return {
        forecast,
        body,
        blocks,
        nowMinutes,
        limits,
        stress,
        eligible,
        idle,
        lowMidday,
        middayFloor,
        ownBars: {
            middayFloor: usualSteps != null,
            idleThreshold: body.typicalSitMinutes != null,
        },
        idleThreshold,
        seatedThreshold: Math.round(idleThreshold * SEATED_SHARE),
        heavyThreshold,
        typicalMeetingMinutes: typicalMeetingMinutes ?? null,
        tomorrow: tomorrow ?? null,
        outOfOffice,
        clearing,
        lastLight,
        heavy: heavyDay,
        depletion: body.depletion ?? 0,
        runAhead: blocks ? (run?.length ?? 0) : forecast.longestRun,
        nextGap,
        run,
        protectSlot,
        moveTarget,
        enoughToday,
        openNow,
    };
}
/** The catch-all arms, tried only when nothing else earned the moment. */
export function fallbackCandidates(read) {
    return armsEligible("fallback", read);
}
/** The workday arms eligible right now, in table order. */
export function movementCandidates(read) {
    return armsEligible("workday", read);
}
/** The arms in `group` that fire on this read, in table order. */
function armsEligible(group, read) {
    return ARM_ROWS.filter((arm) => arm.group === group && !standsDown(arm, read) && arm.when(read)).map((arm) => arm.capability);
}
/**
 * Whether a calendar-shaped arm has nothing to say on this read. Arms that are
 * not about work never stand down; the rest of the table still runs.
 */
function standsDown(arm, read) {
    if (arm.workScope === undefined)
        return false;
    // On leave, nothing about work applies, today's or tomorrow's.
    if (read.outOfOffice)
        return true;
    // Outside working time, an ask about now has nothing to say. An ask about
    // tomorrow's working day still does, which is why the evening heads up runs.
    return arm.workScope === "now" && read.offWork === true;
}
/** Not in a meeting at `minutes` and nothing booked after it: open until the day ends. */
function dayOpenAt(blocks, minutes, dayEndMinutes) {
    return (blocks !== undefined &&
        minutes < dayEndMinutes &&
        !blocks.some((b) => b.startMinutes <= minutes && minutes < b.endMinutes) &&
        !blocks.some((b) => b.startMinutes > minutes));
}
function leadingGap(blocks, nowMinutes) {
    if (blocks.some((b) => b.startMinutes <= nowMinutes && nowMinutes < b.endMinutes))
        return [];
    const next = blocks
        .filter((b) => b.startMinutes > nowMinutes)
        .reduce((soonest, b) => (!soonest || b.startMinutes < soonest.startMinutes ? b : soonest), null);
    if (!next)
        return [];
    return [
        {
            startMinutes: nowMinutes,
            endMinutes: next.startMinutes,
            durationMinutes: next.startMinutes - nowMinutes,
        },
    ];
}
/** The longest unbroken run still ahead: where it starts and how long it is. */
export function longestRunAhead(blocks, nowMinutes, limits) {
    const ahead = blocks
        .filter((b) => b.startMinutes >= nowMinutes)
        .sort((a, b) => a.startMinutes - b.startMinutes);
    let best = null;
    let bestLen = 0;
    let runStart = null;
    let len = 0;
    let bookedTo = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < ahead.length; i++) {
        const block = ahead[i];
        if (!block)
            continue;
        bookedTo = Math.max(bookedTo, block.endMinutes);
        if (!runStart) {
            runStart = block;
            len = 1;
        }
        else {
            len += 1;
        }
        const next = ahead[i + 1];
        const gap = next ? next.startMinutes - bookedTo : Number.POSITIVE_INFINITY;
        if (gap > limits.backToBackMinutes) {
            if (len > bestLen) {
                bestLen = len;
                best = runStart;
            }
            runStart = null;
            len = 0;
        }
    }
    return best ? { start: best, length: bestLen } : null;
}
/** The first block of that run, which is what a "keep time free" sits in front of. */
function freeWindowBefore(blocks, startMinutes, wantMinutes, nowMinutes) {
    const from = startMinutes - wantMinutes;
    if (from < nowMinutes)
        return null;
    const booked = blocks.some((b) => b.startMinutes < startMinutes && b.endMinutes > from);
    return booked ? null : { startMinutes: from, durationMinutes: wantMinutes };
}
export function nextUsableGap(forecast, nowMinutes, limits, 
/** Today's blocks, so the open time before the next meeting counts as a gap. */
blocks = []) {
    const candidates = [...forecast.gaps, ...leadingGap(blocks, nowMinutes)];
    const ahead = candidates
        .filter((g) => g.endMinutes > nowMinutes)
        .map((g) => ({
        startMinutes: Math.max(g.startMinutes, nowMinutes),
        endMinutes: g.endMinutes,
        durationMinutes: g.endMinutes - Math.max(g.startMinutes, nowMinutes),
    }))
        .filter((g) => g.durationMinutes >= limits.usableGapMinutes);
    if (ahead.length === 0)
        return null;
    return ahead.reduce((soonest, g) => (g.startMinutes < soonest.startMinutes ? g : soonest));
}
export function leastImpactfulBlock(blocks, nowMinutes = Number.NEGATIVE_INFINITY) {
    const movable = blocks.filter((b) => b.endMinutes - b.startMinutes >= WORTH_MOVING_MINUTES && b.startMinutes > nowMinutes);
    if (movable.length === 0)
        return null;
    return movable.reduce((best, b) => (impactScore(b) < impactScore(best) ? b : best));
}
function impactScore(block) {
    const owned = block.isOrganiser === undefined ? 1 : block.isOrganiser ? 2 : 0;
    const oneOff = block.recurring === undefined ? 1 : block.recurring ? 0 : 2;
    return owned + oneOff + Math.min(2, block.attendeeCount / 5);
}
//# sourceMappingURL=forecast.js.map